import React, { useState } from 'react';
import { mutate as swrMutate } from 'swr';
import GenericModulePage from '../../components/generic/GenericModulePage';
import { initializeModule } from '../../utils/configInit';
import { deviationConfig } from './deviationConfig';
import { createDeviationValidationSchema } from './deviationValidation';
import { useEntities, useCreateEntity, useUpdateEntity, useDeleteEntity, fetcher } from '../../hooks/useSWR';
import { API_BASE_URL } from '../../config/apiConfig';

/**
 * Initialize the Deviation module using generic utilities
 */
const { config: initializedConfig } = initializeModule(
  deviationConfig,
  createDeviationValidationSchema
);

/**
 * Custom DeviationPage component with custom onSave handler for products
 */
const DeviationPage = () => {
  // Get hooks for data operations
  const { mutate } = useEntities(initializedConfig.api.baseEndpoint);
  const createEntity = useCreateEntity(initializedConfig.api.baseEndpoint);
  const updateEntity = useUpdateEntity(initializedConfig.api.baseEndpoint);
  
  // Hooks for Deviation Actions
  const createAction = useCreateEntity('/deviation-action');
  const updateAction = useUpdateEntity('/deviation-action');
  const deleteAction = useDeleteEntity('/deviation-action');
  
  const customOnSave = async (formData, formMode, currentData, setData) => {
    console.log('🚀 customOnSave CALLED with formData keys:', Object.keys(formData));
    console.log('🚀 customOnSave - formData.products:', formData.products);
    console.log('🚀 customOnSave - formData.deviationActions:', formData.deviationActions);
    
    try {
      // Extract products from formData before any transformations
      let products = formData.products;
      
      // Extract deviationActions from formData before any transformations
      // NOTE: transformData removes deviationActions, so we need to get it from the sync function
      let deviationActions = formData.deviationActions;
      
      console.log('🔍 Initial deviationActions from formData:', deviationActions);
      
      // If deviationActions is undefined or null, try to get it from the sync function
      // This is necessary because transformData removes deviationActions from formData
      if (!deviationActions && typeof window !== 'undefined' && window.__getDeviationActions) {
        const syncData = window.__getDeviationActions();
        console.log('🔍 Sync data from __getDeviationActions:', syncData);
        if (syncData && syncData.localActions && Array.isArray(syncData.localActions)) {
          console.log('🔄 Got deviationActions from sync function:', syncData.localActions);
          console.log('🔍 Sample action from sync:', syncData.localActions[0] ? {
            id: syncData.localActions[0].id,
            implementationStatus: syncData.localActions[0].implementationStatus,
            newImplementationStatus: syncData.localActions[0].newImplementationStatus,
            qaResponsible: syncData.localActions[0].qaResponsible
          } : null);
          deviationActions = syncData.localActions;
        }
      }
      
      // If products is undefined or null, try to get it from the sync function
      if (!products && typeof window !== 'undefined' && window.__getDeviationProducts) {
        const syncData = window.__getDeviationProducts();
        if (syncData && syncData.localProducts && Array.isArray(syncData.localProducts)) {
          console.log('🔄 Got products from sync function:', syncData.localProducts);
          products = syncData.localProducts;
        }
      }
      
      // Ensure products is an array
      if (!Array.isArray(products)) {
        products = [];
      }
      
      // Ensure deviationActions is an array
      if (!Array.isArray(deviationActions)) {
        deviationActions = [];
      }
      
      console.log('🔍 STEP 1 - Extracted products:', products);
      console.log('🔍 STEP 1 - products length:', products.length);
      console.log('🔍 STEP 1 - Extracted deviationActions:', deviationActions);
      console.log('🔍 STEP 1 - deviationActions length:', deviationActions.length);
      
      // Filter out invalid products (those with no data)
      products = products
        .filter(product => {
          const hasData = product && (
            (product.productCode && product.productCode.trim()) ||
            (product.productName && product.productName.trim()) ||
            (product.batchNumber && product.batchNumber.trim()) ||
            product.productQuantity ||
            product.expiryDate
          );
          if (!hasData && product) {
            console.warn('⚠️ Filtering out invalid product (no data):', product);
          }
          return hasData;
        })
        .map(product => ({
          productCode: product.productCode?.trim() || null,
          productName: product.productName?.trim() || null,
          batchNumber: product.batchNumber?.trim() || null,
          productQuantity: product.productQuantity ? parseInt(product.productQuantity) : null,
          expiryDate: product.expiryDate || null
        }));
      
      console.log('🔍 STEP 2 - After filtering and mapping - products:', products);
      console.log('🔍 STEP 2 - After filtering and mapping - products length:', products.length);
      console.log('🔍 STEP 2 - formData keys:', Object.keys(formData));
      
      // Replicate the full handleSave logic from GenericModulePage
      // STEP 1: Validate data
      if (initializedConfig.validateData && typeof initializedConfig.validateData === 'function') {
        const validationResult = await initializedConfig.validateData(formData);
        if (!validationResult.isValid) {
          const errorMessage = Object.values(validationResult.errors).join(', ');
          throw new Error(`Validation failed: ${errorMessage}`);
        }
      }
      
      // STEP 2: Clean data (replicate GenericModulePage logic)
      const systemFieldsToRemove = ['id', 'createdAt', 'updatedAt', '_count'];
      const fieldsToKeep = initializedConfig.fieldsToKeep || [];
      const filteredSystemFieldsToRemove = systemFieldsToRemove.filter(field => !fieldsToKeep.includes(field));
      
      // Get lookup endpoints for relation fields
      const lookupEndpoints = initializedConfig.api?.lookupEndpoints || {};
      const relationFieldsToRemove = [];
      Object.keys(lookupEndpoints).forEach(key => {
        const fieldName = key.replace(/s$/, '');
        relationFieldsToRemove.push(fieldName);
      });
      
      const fieldsToRemove = [
        ...filteredSystemFieldsToRemove, 
        ...relationFieldsToRemove, 
        ...(initializedConfig.fieldsToRemove || [])
      ];
      
      const cleanedData = { ...formData };
      
      // Remove explicitly listed fields
      fieldsToRemove.forEach(field => {
        if (cleanedData.hasOwnProperty(field)) {
          delete cleanedData[field];
        }
      });
      
      // Remove object fields (likely relations) - but preserve arrays
      Object.keys(cleanedData).forEach(key => {
        if (cleanedData[key] && 
            typeof cleanedData[key] === 'object' && 
            !Array.isArray(cleanedData[key]) && 
            cleanedData[key].constructor === Object) {
          delete cleanedData[key];
        }
      });
      
      // Normalize ID fields: send null instead of removing when cleared
      Object.keys(cleanedData).forEach(key => {
        if (key.endsWith('Id')) {
          if (cleanedData[key] === '' || cleanedData[key] === null || cleanedData[key] === undefined) {
            // Explicitly send null so backend can clear existing relation
            cleanedData[key] = null;
          } else {
            const numValue = Number(cleanedData[key]);
            if (!isNaN(numValue)) {
              cleanedData[key] = numValue;
            } else {
              delete cleanedData[key];
            }
          }
        }
      });
      
      // Apply transformData
      const transformedData = initializedConfig.transformData
        ? initializedConfig.transformData(cleanedData, formMode) 
        : cleanedData;
      
      // Add products to transformedData (products are handled by the backend directly)
      if (products && products.length > 0) {
        transformedData.products = products;
      } else {
        // If products array is empty, send empty array to clear existing products
        transformedData.products = [];
      }
      
      // Debug: Log products to verify it's an array
      if (transformedData.products !== undefined) {
        console.log('🔍 products before API call:', {
          value: transformedData.products,
          type: typeof transformedData.products,
          isArray: Array.isArray(transformedData.products),
          length: transformedData.products.length
        });
      }
      
      // Save the deviation
      let savedDeviation;
      if (formMode === 'add') {
        savedDeviation = await createEntity(transformedData);
      } else {
        savedDeviation = await updateEntity(formData.id, transformedData);
      }
      
      // Refresh data
      await mutate();
      
      console.log('✅ Deviation saved successfully:', savedDeviation);
      
      // Handle DeviationActions if deviation was saved successfully
      if (savedDeviation && savedDeviation.id) {
        const deviationId = savedDeviation.id;
        
        // Ensure deviationActions is an array
        const actionsArray = Array.isArray(deviationActions) ? deviationActions : [];
        
        // Fetch existing actions for this deviation
        const fetchActionsByDeviation = async (devId) => {
          try {
            const response = await fetcher(`/deviation-action/by-deviation/${devId}`);
            return Array.isArray(response) ? response : [];
          } catch (error) {
            console.error('Error fetching actions:', error);
            return [];
          }
        };
        
        const existingActions = await fetchActionsByDeviation(deviationId);
        const existingActionIds = new Set(existingActions.map(a => a.id).filter(id => id && !id.toString().startsWith('temp-')));
        
        // Filter and map deviationActions (must have at least description and actionTypeId)
        const actionsToSave = actionsArray
          .filter(action => {
            // Extract actionTypeId from either action.actionTypeId or action.actionType?.id
            const actionTypeId = action.actionTypeId || action.actionType?.id;
            const actionTypeIdNum = actionTypeId ? parseInt(actionTypeId, 10) : null;
            
            const isValid = action && 
              action.description && 
              typeof action.description === 'string' &&
              action.description.trim() !== '' &&
              actionTypeIdNum !== null &&
              !isNaN(actionTypeIdNum);
            if (!isValid) {
              console.warn('⚠️ Filtering out invalid action:', action);
            }
            return isValid;
          })
          .map(action => {
            // Extract actionTypeId from either action.actionTypeId or action.actionType?.id
            const actionTypeId = action.actionTypeId || action.actionType?.id;
            const actionTypeIdNum = actionTypeId ? parseInt(actionTypeId, 10) : null;
            
            // Build the action payload, explicitly including all fields
            const actionPayload = {
              deviationId: deviationId,
              actionTypeId: actionTypeIdNum, // Ensure it's always a number
              description: action.description.trim(),
            };
            
            // Explicitly include optional fields (even if null) so backend can clear them
            actionPayload.responsible = action.responsible && action.responsible.trim() !== '' ? action.responsible : null;
            actionPayload.dueDate = action.dueDate || null;
            actionPayload.closeDate = action.closeDate || null;
            actionPayload.implementationStatus = (action.implementationStatus && action.implementationStatus.trim() !== '') ? action.implementationStatus : null;
            actionPayload.newExtension = action.newExtension || null;
            actionPayload.qaResponsible = action.qaResponsible && action.qaResponsible.trim() !== '' ? action.qaResponsible : null;
            actionPayload.newDueDate = action.newDueDate || null;
            actionPayload.newImplementationStatus = (action.newImplementationStatus && action.newImplementationStatus.trim() !== '') ? action.newImplementationStatus : null;
            actionPayload.qaDate = action.qaDate || null;
            
            console.log(`🔍 Action payload for ${actionTypeIdNum}:`, {
              implementationStatus: actionPayload.implementationStatus,
              newImplementationStatus: actionPayload.newImplementationStatus,
              qaResponsible: actionPayload.qaResponsible
            });
            
            return actionPayload;
          });
        
        console.log('🔍 About to save deviationActions items:', {
          deviationId,
          itemsToSave: actionsToSave.length,
          items: actionsToSave,
          existingActionsCount: existingActions.length,
          sampleAction: actionsToSave[0] ? {
            id: actionsToSave[0].deviationId,
            implementationStatus: actionsToSave[0].implementationStatus,
            newImplementationStatus: actionsToSave[0].newImplementationStatus,
            qaResponsible: actionsToSave[0].qaResponsible
          } : null
        });
        
        // Process each action (create or update)
        let savedActionCount = 0;
        let errorActionCount = 0;
        const actionErrors = [];
        
        for (const action of actionsToSave) {
          try {
            // Find the original action from deviationActions to get the ID
            const originalAction = actionsArray.find(a => {
              const aId = a.actionTypeId || a.actionType?.id;
              const actionId = action.actionTypeId;
              return a.description === action.description && 
                     (aId === actionId || parseInt(aId, 10) === actionId);
            });
            
            const actionId = originalAction?.id;
            const isNew = !actionId || actionId.toString().startsWith('temp-') || !existingActionIds.has(actionId);
            
            if (isNew) {
              // Create new action
              console.log('🔄 Creating new DeviationAction:', action);
              try {
                const savedAction = await createAction(action);
                console.log('✅ Successfully created DeviationAction:', savedAction);
                savedActionCount++;
                // Wait a bit before mutating to ensure backend has processed
                await new Promise(resolve => setTimeout(resolve, 100));
                await swrMutate(`/deviation-action/by-deviation/${deviationId}`);
              } catch (error) {
                console.error(`❌ Failed to create DeviationAction:`, error);
                errorActionCount++;
                actionErrors.push({ action, error: error.message || error.toString() });
              }
            } else if (actionId) {
              // Update existing action
              console.log('🔄 Updating existing DeviationAction:', actionId, action);
              try {
                const savedAction = await updateAction(actionId, action);
                console.log('✅ Successfully updated DeviationAction:', savedAction);
                console.log('🔍 Saved action implementationStatus:', savedAction?.implementationStatus);
                console.log('🔍 Saved action newImplementationStatus:', savedAction?.newImplementationStatus);
                savedActionCount++;
                // Wait a bit before mutating to ensure backend has processed
                await new Promise(resolve => setTimeout(resolve, 100));
                await swrMutate(`/deviation-action/by-deviation/${deviationId}`);
              } catch (error) {
                console.error(`❌ Failed to update DeviationAction:`, error);
                errorActionCount++;
                actionErrors.push({ action, error: error.message || error.toString() });
              }
            }
          } catch (error) {
            console.error('❌ Unexpected error saving action:', error);
            errorActionCount++;
            actionErrors.push({ action, error: error.message });
          }
        }
        
        // Delete actions that were removed (exist in DB but not in formData)
        // This runs even if there are no actions to save, to handle deletions
        const formActionIds = new Set(
          actionsArray
            .map(a => a?.id)
            .filter(id => id && !id.toString().startsWith('temp-'))
            .map(id => id.toString())
        );
        
        console.log('🔍 Delete Check:', {
          actionsArrayLength: actionsArray.length,
          actionsArray: actionsArray,
          formActionIds: Array.from(formActionIds),
          existingActionsCount: existingActions.length,
          existingActionIds: existingActions.map(a => a.id?.toString()),
          existingActions: existingActions
        });
        
        let deletedCount = 0;
        for (const existingAction of existingActions) {
          if (!existingAction || !existingAction.id) {
            console.warn('⚠️ Skipping invalid existingAction:', existingAction);
            continue;
          }
          
          const existingActionIdStr = existingAction.id.toString();
          const stillExists = formActionIds.has(existingActionIdStr);
          
          console.log('🔍 Checking action for deletion:', {
            existingActionId: existingActionIdStr,
            stillExists,
            willDelete: !stillExists
          });
          
          if (!stillExists) {
            try {
              console.log('🔄 Deleting removed action:', existingAction.id);
              await deleteAction(existingAction.id);
              console.log('✅ Successfully deleted action:', existingAction.id);
              deletedCount++;
              await swrMutate(`/deviation-action/by-deviation/${deviationId}`);
            } catch (error) {
              console.error(`❌ Failed to delete DeviationAction:`, error);
              errorActionCount++;
              actionErrors.push({ action: existingAction, error: error.message || error.toString() });
            }
          }
        }
        
        console.log('📊 Action Save Summary:', {
          totalToSave: actionsToSave.length,
          saved: savedActionCount,
          deleted: deletedCount,
          errorCount: errorActionCount,
          errors: actionErrors
        });
        
        if (errorActionCount > 0) {
          console.warn('⚠️ Some action records failed to save:', actionErrors);
        }
      }
      
      // Refresh data again after actions are saved
      await mutate();
      
      return savedDeviation;
    } catch (error) {
      console.error('❌ Save error:', error);
      throw error;
    }
  };
  
  // Pass custom onSave to config (set directly as onSave, not in customHandlers)
  const configWithCustomSave = {
    ...initializedConfig,
    onSave: customOnSave,
    customHandlers: {
      ...initializedConfig.customHandlers
    }
  };
  
  return (
    <GenericModulePage config={configWithCustomSave} />
  );
};

export default DeviationPage;
